import React from "react";
import { format, parseISO } from "date-fns";
import SessionFlag from "./SessionFlag";
import SessionRowActions from "./SessionRowActions";
import { useSession } from "../api/helpers";
import breakPoints from "../app/BreakPoints";
import GradientCard from "../common/GradientCard";
import SessionPresenterCard from "../sessions/SessionPresenterCard";
import useIsFinished from "../sessions/useIsFinished";
import LightTheme from "../theme/LightTheme";

interface Props {
  sessionId: number;
}

const ScheduleItem = ({ sessionId }: Props) => {
  const session = useSession(sessionId);
  const isFinished = useIsFinished(sessionId);

  if (session === undefined) {
    return null;
  }

  return (
    <GradientCard>
      <div className={`session ${isFinished && "opaque"}`}>
        <div className="header">
          <div>
            <div className="dateAndTime">
              <div className="time">
                {format(parseISO(session.startTime), "h:mmaaa")} -{" "}
                {format(parseISO(session.endTime), "h:mmaaa")}
              </div>
              <p className="date">
                {format(parseISO(session.startTime), "eeee, MMMM do")}
              </p>
            </div>
            <div className="calSessionMainTitle">
              <SessionFlag sessionId={sessionId} />
              <h3>{session.title}</h3>
            </div>
          </div>
          <SessionRowActions sessionId={sessionId} />
        </div>
        <div>
          <p>{session.description}</p>
          <div className="sessionTagsWrapper">
            {session.tags.map((tag) => (
              <div key={tag} className="tag">
                <div className="tagText">{tag}</div>
              </div>
            ))}
          </div>
          <div className="presenterList">
            {session.presenters.map((presenter) => (
              <SessionPresenterCard
                key={presenter.profileId}
                avatarUrl={presenter.avatarUrl}
                name={presenter.name}
                title={presenter.title}
              />
            ))}
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .session {
            padding: 30px;
            display: flex;
            flex-direction: column;
          }
          .header {
            display: flex;
            justify-content: space-between;
          }
          .opaque {
            color: #3a3a3b;
          }
          .opaque p {
            opacity: 0.5;
          }
          .opaque .time {
            opacity: 0.5;
          }
          .opaque h3 {
            opacity: 0.5;
          }
          .opaque .tag {
            opacity: 0.5;
          }
          .opaque .presenterList {
            opacity: 0.5;
          }
          .tagText {
            font-weight: normal;
            font-size: 0.7rem;
            text-align: center;
            color: #3a3a3b;
          }
          .tag {
            width: auto;
            height: 20px;
            border: 1px solid #3a3a3b;
            border-radius: 30px;
            margin: 0 20px 0 0;
            padding: 0 5px 0 5px;
          }
          h3 {
            margin: 0;
            font-weight: bold;
            display: flex;
            color: #3a3a3b;
          }
          .calSessionMainTitle {
            display: flex;
            align-items: center;
            padding: 10px 0;
          }
          p {
            font-weight: normal;
            font-size: 0.9rem;
            color: ${LightTheme.font.secondary.color};
          }
          .presenterList {
            display: flex;
            margin: 25px 0 0 0;
            flex-wrap: wrap;
            width: 80%;
          }
          .sessionTagsWrapper {
            display: flex;
            margin: 20px 0 0 0;
          }
          .dateAndTime {
            display: flex;
            align-items: center;
          }
          .time {
            padding: 0 5px 0 0;
            font-weight: bold;
            font-size: 0.7rem;
            color: #3a3a3b;
          }
          .date {
            margin: 0;
          }
          @media only screen and (max-width: ${breakPoints.small}px) {
            .header {
              flex-direction: column;
            }
          }
        `}
      </style>
    </GradientCard>
  );
};

export default ScheduleItem;
